import React, {useState, useEffect, useRef} from 'react';
import {checkCookies, eraseCookie, getMotoragazziCookies} from './utils/cookies';
import {css} from 'aphrodite';
import {ReactComponent as ArrowDownIcon} from 'src/icons/arrow-down.svg';
import {ReactComponent as CrossIcon} from 'src/icons/cross.svg';
import {useLocalization} from 'packages/localization/localization';

import IsLocalStorageAvil from './utils/isLocalStorage';
import Styles from './cookiesFull.jss';
import Tooltip from 'packages/tooltip';
import showWarning from 'packages/lightbox/warning.react';
import Portal from 'lib/portal/portal.react';
import SimpleCollapse from 'packages/motion/SimpleCollapse.react';
import {MakeButton, PrimaryFilled, GrayFilled, WhiteFilled} from 'src/themes/button';
import {LightLightbox} from 'src/themes/lightbox';
import {Global} from 'src/globals.d';
import Toggle from 'packages/forms/Toggle';
import {Trans} from 'react-i18next';

export default function CookiesFull() {
    const {t, tReady} = useLocalization('cookies');
    const lightbox = useRef<HTMLDivElement | null>(null);

    //@ts-ignore
    const globalPrivacyControl = navigator.globalPrivacyControl || false;

    const [showDetails, setShowDetails] = useState<boolean>(false);
    const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
    const [showPerformance, setShowPerformance] = useState<boolean>(false);
    const [analytics, setAnalytics] = useState<boolean>(false);
    const [performance, setPerformance] = useState<boolean>(false);

    const isCookieEnabled = checkCookies();

    const [shouldShowCookies, setShouldShowCookies] = useState(false);

    const removeAnalytics = () => {
        eraseCookie('_gid');
        eraseCookie('_ga_9K53J71P74');
        eraseCookie('_ga');
    };

    const removePerformance = () => {
        eraseCookie('_fbp');
    };

    const rejectAll = () => {
        if (IsLocalStorageAvil()) {
            window.localStorage.setItem('motoragazzi_cookies_analytics', '0');
            window.localStorage.setItem('motoragazzi_cookies_performance', '0');
            window.localStorage.setItem('motoragazzi_cookies', '1');
        }

        //@ts-ignore
        window['ga-disable-G-9K53J71P74'] = true;
        removeAnalytics();
        removePerformance();
        setShouldShowCookies(false);

        if (Global.loadedGA) {
            if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                window.gtag('consent', 'default', {
                    analytics_storage: 'denied',
                    wait_for_update: 2000,
                });
            }
        }

        window.location.reload();
    };

    const save = () => {
        if (IsLocalStorageAvil()) {
            window.localStorage.setItem('motoragazzi_cookies_performance', performance ? '1' : '0');
            window.localStorage.setItem('motoragazzi_cookies_analytics', analytics ? '1' : '0');
            window.localStorage.setItem('motoragazzi_cookies', '1');
        }

        if (!performance) {
            removePerformance();
        }
        if (!analytics) {
            //@ts-ignore
            window['ga-disable-G-9K53J71P74'] = true;
            removeAnalytics();
        }

        setShouldShowCookies(false);

        window.location.reload();
    };

    const saveAll = () => {
        if (IsLocalStorageAvil()) {
            window.localStorage.setItem('motoragazzi_cookies_analytics', '1');
            window.localStorage.setItem('motoragazzi_cookies_performance', '1');
            window.localStorage.setItem('motoragazzi_cookies', '1');
        }

        setShouldShowCookies(false);

        window.location.reload();
    };

    const clickDisabled = () => {
        showWarning(t('warning'), LightLightbox);
    };

    useEffect(() => {
        document.body.style.overflow = showDetails ? 'hidden' : '';
    }, [showDetails]);

    useEffect(() => {
        const updatePageScroll = () => {
            if (shouldShowCookies) {
                setTimeout(() => {
                    if (document.body) {
                        document.body.style.overflow = 'hidden';
                    }
                }, 100);
            } else {
                if (document.body) {
                    document.body.style.overflow = '';
                }
            }
        };

        updatePageScroll();
    }, [shouldShowCookies]);

    useEffect(() => {
        if (getMotoragazziCookies() === '0') {
            setShouldShowCookies(true);
        }
    }, []);

    const CheckboxParent = !isCookieEnabled ? 'button' : 'div';

    if (!tReady) {
        return null;
    }

    return shouldShowCookies ? (
        <Portal>
            <div
                className={css(Styles.main)}
                data-testid="cookies-lightbox"
            >
                <div className={css(Styles.block)}>
                    <div className={css(Styles.blockContent)}>
                        <h3 className={css(Styles.title)}>{t('title')}</h3>
                        <p className={css(Styles.text)}>{t('text')}</p>
                        <p className={css(Styles.text)}>
                            { /* prettier-ignore */ }
                            <Trans
                                t={t}
                                i18nKey={"accept"}
                            >
                                By continuing to browse the site, you accept our use of cookies, <a href="https://motoragazzi.com/files/public/privacy_policy.pdf" target="_blank" rel="noopener noreferrer" className={css(Styles.link)} data-autofocus>Privacy Policy</a> and <a href="https://motoragazzi.com/files/public/terms_and_conditions.pdf" target="_blank" rel="noopener noreferrer" className={css(Styles.link)}>Terms of Service</a>.
                            </Trans>
                        </p>
                        <div className={css(Styles.warning)}>{t('info')}</div>
                        <div className={css(Styles.nav)}>
                            <button
                                type="button"
                                className={css(MakeButton(GrayFilled), Styles.btn)}
                                onClick={() => setShowDetails(!showDetails)}
                                tabIndex={0}
                                data-testid="cookies-customize-button"
                            >
                                <span>{t('customize')}</span>
                            </button>
                            <button
                                type="button"
                                className={css(MakeButton(GrayFilled), Styles.btn)}
                                onClick={rejectAll}
                                tabIndex={0}
                                data-testid="cookies-reject-button"
                            >
                                {t('reject')}
                            </button>
                            <button
                                type="button"
                                className={css(MakeButton(PrimaryFilled), Styles.btn)}
                                onClick={saveAll}
                                tabIndex={0}
                                data-testid="cookies-accept-button"
                            >
                                {t('agree')}
                            </button>
                        </div>
                    </div>
                </div>
                {showDetails ? (
                    <div className={css(Styles.main, Styles.mainExtra)}>
                        <div className={css(Styles.wrapper)}>
                            <div
                                ref={lightbox}
                                className={css(Styles.lightbox)}
                            >
                                <button
                                    className={css(MakeButton(WhiteFilled), Styles.close)}
                                    onClick={() => setShowDetails(false)}
                                    aria-label={t('close')}
                                >
                                    <CrossIcon
                                        role="img"
                                        aria-hidden="true"
                                    />
                                </button>

                                <h3 className={css(Styles.title)}>{t('customize-title')}</h3>
                                <p className={css(Styles.text, Styles.textLight)}>{t('customize-text')}</p>
                                <ul className={css(Styles.detailsList)}>
                                    <li>
                                        <div className={css(Styles.detailsItemTitle)}>
                                            <div className={css(Styles.detailsItemTitleLeft)}>
                                                <div className={css(Styles.btnIconBlock)} />
                                                <span>{t('necessary')}</span>
                                            </div>
                                            <div className={css(Styles.detailsItemTitleLabel)}>{t('always-on')}</div>
                                        </div>

                                        <div className={css(Styles.detailsItem)}>
                                            <p className={css(Styles.detailsItemCookie)}>
                                                <i>_GRECAPTCHA</i>. {t('necessary-cookie-description')}
                                            </p>
                                        </div>
                                    </li>
                                    <li>
                                        <div className={css(Styles.detailsItemTitle)}>
                                            <button
                                                type="button"
                                                className={css(Styles.detailsItemTitleLeft)}
                                                onClick={() => setShowPerformance(!showPerformance)}
                                            >
                                                <ArrowDownIcon
                                                    className={css(
                                                        Styles.btnIcon,
                                                        Styles.btnIconDetails,
                                                        showPerformance && Styles.btnIconOpened
                                                    )}
                                                    width="14"
                                                    height="14"
                                                    role="img"
                                                    aria-hidden="true"
                                                />
                                                <span>{t('performance')}</span>
                                            </button>
                                            <CheckboxParent
                                                type={!isCookieEnabled ? 'button' : undefined}
                                                onClick={!isCookieEnabled ? clickDisabled : undefined}
                                                className={!isCookieEnabled ? css(Styles.checkboxParent) : undefined}
                                            >
                                                <Tooltip
                                                    disabled={!globalPrivacyControl}
                                                    content={t('gpc-performance-tooltip')}
                                                >
                                                    <span>
                                                        <Toggle
                                                            value={
                                                                globalPrivacyControl
                                                                    ? false
                                                                    : isCookieEnabled
                                                                    ? performance
                                                                    : false
                                                            }
                                                            disabled={!isCookieEnabled || globalPrivacyControl}
                                                            onChange={() => setPerformance(!performance)}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </CheckboxParent>
                                        </div>
                                        <SimpleCollapse
                                            collapse={!showPerformance}
                                            duration={200}
                                        >
                                            <div className={css(Styles.detailsItem)}>
                                                <p className={css(Styles.detailsItemCookie)}>
                                                    <i>Meta Pixel</i>. {t('performance-cookie-description')}
                                                </p>
                                                <p className={css(Styles.detailsItemCookie)}>
                                                    <i>Google Maps</i>. {t('performance-cookie-description-2')}
                                                </p>
                                                <p className={css(Styles.detailsItemCookie)}>
                                                    <i>YouTube (embedded)</i>. {t('performance-cookie-description-3')}
                                                </p>
                                            </div>
                                        </SimpleCollapse>
                                    </li>
                                    <li>
                                        <div className={css(Styles.detailsItemTitle)}>
                                            <button
                                                type="button"
                                                className={css(Styles.detailsItemTitleLeft)}
                                                onClick={() => setShowAnalytics(!showAnalytics)}
                                            >
                                                <ArrowDownIcon
                                                    className={css(
                                                        Styles.btnIcon,
                                                        Styles.btnIconDetails,
                                                        showAnalytics && Styles.btnIconOpened
                                                    )}
                                                    width="14"
                                                    height="14"
                                                    role="img"
                                                    aria-hidden="true"
                                                />
                                                <span>{t('analytics')}</span>
                                            </button>
                                            <CheckboxParent
                                                type={!isCookieEnabled ? 'button' : undefined}
                                                onClick={!isCookieEnabled ? clickDisabled : undefined}
                                                className={!isCookieEnabled ? css(Styles.checkboxParent) : undefined}
                                            >
                                                <Tooltip
                                                    disabled={!globalPrivacyControl}
                                                    content={t('gpc-analytics-tooltip')}
                                                >
                                                    <span>
                                                        <Toggle
                                                            value={
                                                                globalPrivacyControl
                                                                    ? false
                                                                    : isCookieEnabled
                                                                    ? analytics
                                                                    : false
                                                            }
                                                            onChange={() => setAnalytics(!analytics)}
                                                            disabled={!isCookieEnabled || globalPrivacyControl}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            </CheckboxParent>
                                        </div>
                                        <SimpleCollapse
                                            collapse={!showAnalytics}
                                            duration={200}
                                        >
                                            <div className={css(Styles.detailsItem)}>
                                                <p className={css(Styles.detailsItemCookie)}>
                                                    <i>_gid</i>, <i>_ga</i>, <i>_ga__ga_BCMSXCEQE0</i>.{' '}
                                                    {t('analytics-cookie-description')}
                                                </p>
                                            </div>
                                        </SimpleCollapse>
                                    </li>
                                </ul>
                                <div className={css(Styles.detailsNav)}>
                                    <button
                                        type="button"
                                        className={css(MakeButton(PrimaryFilled))}
                                        onClick={save}
                                    >
                                        {t('save')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </Portal>
    ) : null;
}
